import { useTranslation } from "react-i18next";
import { FC, ReactNode, useEffect, useState } from "react";
import { useFormik } from "formik";
import { CustomModal } from "../../../components/CustomModal/CustomModal";
import { CustomTextInput } from "../../../components/CustomTextInput/CustomTextInput";
import useStyles from "./styles";
import { validationSchemaUpdateCollaborator } from "./validationSchemaUpdateCollaborator";
import { getCurrentFleetId } from "../../../utils/getCurrentFleet";
import {
  useAssignDepartementCollab,
  useGetAllDepartments,
} from "../../../api/departments";
import { SelectChangeEvent } from "@mui/material";
import { CustomTextSelect } from "../../../components/CustomTextSelect/CustomTextSelect";
import { getOptionsFromObjects } from "../../../utils/utils";
import { useUpdateUserEmployee } from "../../../api/employee";

type props = {
  open: boolean;
  employee: TEmployeeOne | null;
  onCancel?: () => void;
  onClose?: () => void;
};

export const ModalUpdateCollaborator: FC<props> = ({
  open,
  employee,
  onCancel,
  onClose,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { departments } = useGetAllDepartments(getCurrentFleetId());
  const { mutate: updateUserAdmin, isPending } = useUpdateUserEmployee(
    getCurrentFleetId(),
    employee?.user_id ?? 0,
    onClose,
    () => {}
  );
  const { mutate: assignCollabDepartement } = useAssignDepartementCollab(
    getCurrentFleetId(),
    employee?.user_id ?? 0
  );

  const formik = useFormik({
    initialValues: {
      firstname: employee?.firstname ?? null,
      lastname: employee?.lastname ?? null,
      email: employee?.email ?? null,
      department_id: employee?.department_id,
    },
    validationSchema: validationSchemaUpdateCollaborator,
    onSubmit: (values) => {
      updateUserAdmin({
        email: values.email ?? "",
        firstname: values.firstname ?? "",
        lastname: values.lastname ?? "",
      });
    },
  });

  // BUGFIX (validation.required + "8" shown in the Service dropdown):
  //
  // MUI Select's SelectChangeEvent.target.value is the raw MenuItem `value`
  // prop (in our case, the department id as a number/string), NOT the whole
  // option object. The previous code cast it to `{ label, value }` and then
  // read `.value`, which was undefined, sent Number(undefined) = NaN, which
  // JSON-serialized to null, which the backend rejected with a required
  // validation error — the toast the user was seeing.
  //
  // MUI Select also needs a scalar `value` to match a MenuItem by identity
  // and render the correct label. Storing `{label, value}` in state broke
  // that match, so the Select fell back to displaying the raw id.
  //
  // Fix on both fronts: keep only the department id (scalar) in state and
  // read event.target.value as a scalar in the handler.
  const [departmentId, setDepartmentId] = useState<number | string>("");

  useEffect(() => {
    if (employee !== null) {
      formik.setFieldValue("firstname", employee.firstname);
      formik.setFieldValue("lastname", employee.lastname);
      formik.setFieldValue("email", employee.email);
      formik.setFieldValue("department_id", employee.department_id);
      // Empty string when no department is assigned yet — that's what MUI
      // Select needs to display an unselected state cleanly.
      setDepartmentId(employee.department_id ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee]);

  const onChangeSelectDep = (event: SelectChangeEvent<any>, _: ReactNode) => {
    const nextDepartmentId = event.target.value;

    // Ignore clears / empty selections — the endpoint requires a real id,
    // and firing the mutation with null would trigger the same 422 the
    // original bug produced.
    if (nextDepartmentId === "" || nextDepartmentId === null || nextDepartmentId === undefined) {
      return;
    }

    // No-op if the user re-selected the current department: avoids a
    // useless PUT and a duplicate success toast.
    if (nextDepartmentId === departmentId) {
      return;
    }

    formik.setFieldValue("department_id", nextDepartmentId);
    setDepartmentId(nextDepartmentId);
    assignCollabDepartement({
      department_id: Number(nextDepartmentId),
    });
  };

  const onSave = () => {
    formik.handleSubmit();
  };

  return (
    <CustomModal
      open={open}
      onCancel={onCancel}
      title={t("collaborator.collaborator", "Collaborator")}
      onClose={onClose}
      onSave={onSave}
      loadingSave={isPending}
    >
      <div className={classes.containerText}>
        <CustomTextInput
          label={t("collaborator.name", "Name")}
          placeholder={t("collaborator.name", "Name")}
          marginBottom={"0px"}
          name="lastname"
          value={formik.values.lastname}
          onChange={formik.handleChange}
        />
        <CustomTextInput
          label={t("collaborator.firstName", "Firstname")}
          placeholder={t("collaborator.firstName", "Firstname")}
          marginBottom={"0px"}
          name="firstname"
          value={formik.values.firstname}
          onChange={formik.handleChange}
        />
        <CustomTextInput
          label={t("collaborator.email", "Email")}
          placeholder={t("collaborator.email", "Email")}
          marginBottom={"0px"}
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
        />
        <CustomTextSelect
          label={t("collaborator.service", "Service")}
          placeholder={t("collaborator.service", "Service")}
          value={departmentId}
          onChange={onChangeSelectDep}
          options={getOptionsFromObjects(departments, "name", "id")}
          // Same guard as the collaborator wizard: when department_id points
          // outside this company's list, show the name the API already gave
          // us rather than letting the raw id surface.
          fallbackLabel={employee?.department ?? undefined}
        />
      </div>
    </CustomModal>
  );
};
